import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

type StudentInfo = {
  id: number | null;
  fullName: string;
  email: string;
  phone: string;
  studentClass: string;
  course: string;
  profileImage: string;
};

type Course = {
  id: number;
  title: string;
  category?: string;
  teacher?: string;
  price?: number;
  image_url?: string;
  progress?: number;
};

type LiveStatus = "Live" | "Scheduled" | "Completed" | "Cancelled";

type LiveClass = {
  id: number;
  title: string;
  course: string;
  batch: string;
  teacher: string;
  subject: string;
  platform: string;
  meetingLink: string;
  roomName?: string;
  date: string;
  startTime: string;
  duration: number;
  status: LiveStatus;
  recordingUrl?: string;
  materials?: string;
};

type SubjectItem = {
  title: string;
  desc: string;
  lessons: number;
  icon: string;
};

// Update: Added URL property to open actual files
type MaterialItem = {
  title: string;
  desc: string;
  type: "PDF" | "Notes" | "Assignment";
  url?: string; 
};

type TabType =
  | "overview"
  | "subjects"
  | "live"
  | "recordings"
  | "notes"
  | "tests"
  | "progress";

// const PROFILE_API = "http://localhost:9090/api/profile";
// const COURSES_API = "http://localhost:9090/api/courses";
// const LIVE_CLASS_API = "http://localhost:9091/api/student/live-classes";

const PROFILE_API = "https://node-api.mediitacademy.com/api/profile";
const COURSES_API = "https://node-api.mediitacademy.com/api/courses";
const LIVE_CLASS_API = "https://api.mediitacademy.com/api/student/live-classes";

const isValidTab = (value: string | null): value is TabType => {
  return [
    "overview",
    "subjects",
    "live",
    "recordings",
    "notes",
    "tests",
    "progress",
  ].includes(value || "");
};

const CourseDetails: React.FC = () => {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();

  const [student, setStudent] = useState<StudentInfo>({
    id: null,
    fullName: "",
    email: "",
    phone: "",
    studentClass: "",
    course: "",
    profileImage: "",
  });

  const [courses, setCourses] = useState<Course[]>([]);
  const [liveClasses, setLiveClasses] = useState<LiveClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const tab = searchParams.get("tab");
    return isValidTab(tab) ? tab : "overview";
  });

  // NEW STATE: For handling Content Viewer Modal
  const [viewerContent, setViewerContent] = useState<{
    type: "video" | "pdf";
    url: string;
    title: string;
  } | null>(null);

  const getToken = () => localStorage.getItem("token") || "";

  const getStoredStudentId = () => {
    const directId =
      localStorage.getItem("studentId") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("id");

    if (directId) return Number(directId);

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return Number(user?.id || user?.studentId || 1);
    } catch {
      return 1;
    }
  };

  const normalizeCourse = (item: any): Course => {
    return {
      id: Number(item.id || item.courseId || 0),
      title: item.title || item.name || item.courseName || "Course",
      category: item.category || item.studentClass || item.className || "Course",
      teacher:
        item.teacher || item.teacherName || item.mentor || "Assigned Teacher",
      price: Number(item.price || item.amount || 0),
      image_url: item.image_url || item.imageUrl || item.thumbnail || "",
      progress: Number(item.progress || item.completedPercentage || 0),
    };
  };

  const normalizeStatus = (value: any): LiveStatus => {
    const status = String(value || "Scheduled").toLowerCase();
    if (status === "live") return "Live";
    if (status === "completed") return "Completed";
    if (status === "cancelled" || status === "canceled") return "Cancelled";
    return "Scheduled";
  };

  const normalizeTime = (value: any) => {
    if (!value) return "";
    return String(value).slice(0, 5);
  };

  const normalizeLiveClass = (item: any): LiveClass => {
    return {
      id: Number(item.id),
      title: item.title || "Live Class",
      course: item.course || item.courseTitle || "",
      batch: item.batch || item.batchName || "",
      teacher: item.teacher || item.teacherName || "Assigned Teacher",
      subject: item.subject || item.subjectName || "Subject",
      platform: item.platform || "Website Live",
      meetingLink: item.meetingLink || item.meeting_link || "",
      roomName: item.roomName || item.room_name || "",
      date: item.date || item.classDate || item.class_date || "",
      startTime: normalizeTime(item.startTime || item.start_time),
      duration: Number(item.duration || 60),
      status: normalizeStatus(item.status),
      recordingUrl: item.recordingUrl || item.recording_url || "",
      materials: item.materials || "",
    };
  };

  const fetchProfile = async () => {
    const token = getToken();

    if (!token) {
      window.location.href = "/login";
      return null;
    }

    const response = await fetch(PROFILE_API, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || "Profile loading failed");
    }

    const nextStudent: StudentInfo = {
      id: Number(data.id || data.user_id || getStoredStudentId()),
      fullName: data.full_name || data.name || "Student",
      email: data.email || "",
      phone: data.mobile_number || data.phone || "",
      studentClass: data.school || data.studentClass || data.class || "",
      course: data.course || "",
      profileImage: data.profile_image
        ? `https://node-api.mediitacademy.com${data.profile_image}`
        : "",
    };

    setStudent(nextStudent);
    localStorage.setItem("studentId", String(nextStudent.id || 1));
    return nextStudent;
  };

  const fetchCourses = async () => {
    const response = await fetch(COURSES_API);
    if (!response.ok) throw new Error("Courses loading failed");

    const data = await response.json();
    const normalized = Array.isArray(data) ? data.map(normalizeCourse) : [];

    setCourses(normalized);
    return normalized;
  };

  const fetchLiveClasses = async (studentId: number) => {
    try {
      const response = await fetch(`${LIVE_CLASS_API}/my?studentId=${studentId}`);
      if (!response.ok) throw new Error("Student live classes API failed");

      const data = await response.json();
      setLiveClasses(Array.isArray(data) ? data.map(normalizeLiveClass) : []);
    } catch {
      try {
        const fallback = await fetch(LIVE_CLASS_API);
        if (fallback.ok) {
          const data = await fallback.json();
          setLiveClasses(Array.isArray(data) ? data.map(normalizeLiveClass) : []);
        }
      } catch {
        setLiveClasses([]);
      }
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const profile = await fetchProfile();
      await Promise.allSettled([
        fetchCourses(),
        fetchLiveClasses(Number(profile?.id || getStoredStudentId())),
      ]);
    } catch (error) {
      console.error("Course details loading error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (isValidTab(tab)) setActiveTab(tab);
  }, [searchParams]);

  const course = useMemo<Course | null>(() => {
    const profileCourseName = student.course?.toLowerCase().trim();
    const profileHasRealCourse = profileCourseName && !profileCourseName.includes("demo");
    const matchedByProfileCourse = profileHasRealCourse ? courses.find((item) => item.title.toLowerCase().trim() === profileCourseName) : null;
    const matchedById = courses.find((item) => String(item.id) === String(courseId));

    if (matchedByProfileCourse) return matchedByProfileCourse;
    if (profileHasRealCourse) return { id: Number(courseId || matchedById?.id || 0), title: student.course, category: student.studentClass || "Student Course", teacher: "Assigned Teacher", price: 0, image_url: "", progress: 0 };
    return matchedById || null;
  }, [courses, courseId, student.course, student.studentClass]);

  const subjects = useMemo<SubjectItem[]>(() => {
    const title = `${course?.title || ""} ${course?.category || ""}`.toLowerCase();
    if (title.includes("neet")) return [ { title: "Physics", desc: "Concepts, numericals aur previous year pattern practice.", lessons: 24, icon: "Φ" }, { title: "Chemistry", desc: "Physical, Organic aur Inorganic NCERT-focused classes.", lessons: 26, icon: "⚗" }, { title: "Biology", desc: "NCERT line-by-line Biology, diagrams aur revision notes.", lessons: 32, icon: "DNA" } ];
    if (title.includes("jee")) return [ { title: "Physics", desc: "Laws, formulas, numericals aur exam-level problem solving.", lessons: 28, icon: "Φ" }, { title: "Chemistry", desc: "Physical, Organic aur Inorganic topic-wise preparation.", lessons: 26, icon: "⚗" }, { title: "Mathematics", desc: "Algebra, calculus, coordinate geometry aur practice sheets.", lessons: 30, icon: "∑" } ];
    return [ { title: "Mathematics", desc: "Basic concept clarity, formulas aur school exam practice.", lessons: 18, icon: "∑" }, { title: "Science", desc: "NCERT-based science concepts with examples and notes.", lessons: 20, icon: "⚗" }, { title: "Reasoning", desc: "Foundation level reasoning and Olympiad-style practice.", lessons: 12, icon: "★" } ];
  }, [course?.title, course?.category]);

  const filteredLiveClasses = useMemo(() => {
    if (!course) return [];
    const courseText = `${course.title} ${course.category || ""}`.toLowerCase().trim();
    return liveClasses.filter((item) => {
      const liveText = `${item.course} ${item.batch} ${item.subject} ${item.title}`.toLowerCase().trim();
      if (!liveText) return false;
      if (liveText.includes(course.title.toLowerCase().trim())) return true;
      if (courseText.includes("jee") && liveText.includes("jee")) return true;
      if (courseText.includes("neet") && liveText.includes("neet")) return true;
      if (courseText.includes("class 7") && liveText.includes("class 7")) return true;
      if (courseText.includes("class 8") && liveText.includes("class 8")) return true;
      if (courseText.includes("class 9") && liveText.includes("class 9")) return true;
      if (courseText.includes("class 10") && liveText.includes("class 10")) return true;
      if (courseText.includes("class 12") && liveText.includes("class 12")) return true;
      return false;
    });
  }, [course, liveClasses]);

  const liveNowClasses = filteredLiveClasses.filter((item) => item.status === "Live");
  const upcomingClasses = filteredLiveClasses.filter((item) => item.status === "Scheduled");
  const completedClasses = filteredLiveClasses.filter((item) => item.status === "Completed");
  const recordingClasses = completedClasses.filter((item) => item.recordingUrl);

  const materials = useMemo<MaterialItem[]>(() => {
    return subjects.flatMap((subject) => [
      {
        title: `${subject.title} Quick Revision Notes`,
        desc: "Chapter-wise important formulas, definitions aur key points.",
        type: "Notes" as const,
        url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf" // Placeholder URL for PDF
      },
      {
        title: `${subject.title} Practice Assignment`,
        desc: "Homework, DPP aur exam-style practice questions.",
        type: "Assignment" as const,
        url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf" // Placeholder URL for PDF
      },
    ]);
  }, [subjects]);

  const joinLiveClass = async (liveClass: LiveClass) => {
    const studentId = student.id || getStoredStudentId();
    const studentName = student.fullName || "Student";
    try {
      await fetch(
        `${LIVE_CLASS_API}/${liveClass.id}/join?studentId=${studentId}&studentName=${encodeURIComponent(studentName)}`,
        { method: "POST" }
      );
    } catch {} // Room open karna priority hai.
    window.location.href = `/student-dashboard/live-class-room/${liveClass.id}`;
  };

  const tabs: { key: TabType; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "subjects", label: "Subjects" },
    { key: "live", label: "Live Classes" },
    { key: "recordings", label: "Recordings" },
    { key: "notes", label: "Study Notes" },
    { key: "tests", label: "Tests" },
    { key: "progress", label: "Progress" },
  ];

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#edf4ff]">
        <div className="rounded-[28px] bg-white px-8 py-7 text-center shadow-sm">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
          <p className="mt-4 text-sm font-black text-slate-600">Loading course details...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#edf4ff]">
        <div className="max-w-lg rounded-[30px] bg-white p-8 text-center shadow-sm">
          <h2 className="text-2xl font-black text-slate-950">Course not found</h2>
          <p className="mt-2 text-sm font-semibold text-slate-500">Course data nahi mila. Please back jaake course dobara open karo.</p>
          <button onClick={() => (window.location.href = "/student-dashboard/my-courses")} className="mt-6 rounded-2xl bg-blue-600 px-6 py-3 text-sm font-black text-white">Back to My Courses</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#edf4ff] text-[#101827] font-['Inter']">
      
      {/* 🚀 MODAL INTEGRATION: Content Viewer */}
      {viewerContent && (
        <ContentViewerModal 
          content={viewerContent} 
          onClose={() => setViewerContent(null)} 
        />
      )}

      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/95 px-4 py-3 shadow-sm backdrop-blur-xl sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <button onClick={() => (window.location.href = "/student-dashboard/my-courses")} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-lg font-black text-white hover:bg-blue-700">←</button>
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-blue-700">Course Details</p>
              <h1 className="truncate text-xl font-black text-slate-950 sm:text-2xl">{course.title}</h1>
            </div>
          </div>
          <button onClick={() => (window.location.href = "/student-dashboard")} className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white hover:bg-blue-600">Dashboard</button>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] space-y-6 p-4 sm:p-6 lg:p-8">
        <section className="relative overflow-hidden rounded-[40px] bg-gradient-to-br from-[#08234a] via-[#123b7a] to-[#2563eb] p-7 text-white shadow-sm sm:p-8">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 left-24 h-60 w-60 rounded-full bg-cyan-300/20 blur-3xl" />
          <div className="relative">
            <p className="text-sm font-black uppercase tracking-wide text-blue-100">Active Course</p>
            <h2 className="mt-3 max-w-4xl text-3xl font-black tracking-[-0.04em] sm:text-5xl">{course.title}</h2>
            <p className="mt-3 text-sm font-semibold text-blue-100">{course.category || student.studentClass || "Course"} • Mentor: {course.teacher || "Assigned Teacher"}</p>
            
            {/* ENROLL / CHECKOUT BUTTON */}
            <div className="mt-8 mb-4">
              <button
                onClick={() => (window.location.href = `/course-purchase/${course.id}`)}
                className="rounded-2xl bg-white px-8 py-4 text-sm font-black text-blue-900 shadow-xl transition-all hover:-translate-y-1 hover:shadow-2xl hover:bg-blue-50"
              >
                Enroll / Checkout Now →
              </button>
            </div>

            <div className="mt-7 grid max-w-5xl gap-4 sm:grid-cols-4">
              <StatCard label="Subjects" value={String(subjects.length)} />
              <StatCard label="Live Classes" value={String(liveNowClasses.length + upcomingClasses.length)} />
              <StatCard label="Recordings" value={String(recordingClasses.length)} />
              <StatCard label="Tests" value="3" />
            </div>
          </div>
        </section>

        <section className="rounded-[34px] bg-white p-4 shadow-sm">
          <div className="flex gap-3 overflow-x-auto pb-2">
            {tabs.map((tab) => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`shrink-0 rounded-2xl px-5 py-3 text-sm font-black transition ${activeTab === tab.key ? "bg-blue-600 text-white" : "bg-slate-50 text-slate-600 hover:bg-blue-50 hover:text-blue-700"}`}>
                {tab.label}
              </button>
            ))}
          </div>
        </section>

        {activeTab === "overview" && <OverviewTab course={course} student={student} subjects={subjects} liveCount={liveNowClasses.length + upcomingClasses.length} recordingsCount={recordingClasses.length} setActiveTab={setActiveTab} />}
        {activeTab === "subjects" && <SubjectsTab subjects={subjects} />}
        {activeTab === "live" && <LiveTab liveNowClasses={liveNowClasses} upcomingClasses={upcomingClasses} joinLiveClass={joinLiveClass} />}
        {/* Pass setViewerContent to Recordings and Notes */}
        {activeTab === "recordings" && <RecordingsTab completedClasses={completedClasses} onWatch={(url: string, title: string) => setViewerContent({ type: "video", url, title })} />}
        {activeTab === "notes" && <NotesTab materials={materials} onView={(url: string, title: string) => setViewerContent({ type: "pdf", url, title })} />}
        {activeTab === "tests" && <TestsTab course={course} />}
        {activeTab === "progress" && <ProgressTab course={course} subjects={subjects} completedClasses={completedClasses} recordingsCount={recordingClasses.length} />}
      </main>
    </div>
  );
};

// ----------------------------------------------------
// 🚀 NEW COMPONENT: CONTENT VIEWER MODAL
// ----------------------------------------------------
const ContentViewerModal = ({
  content,
  onClose,
}: {
  content: { type: "video" | "pdf"; url: string; title: string };
  onClose: () => void;
}) => {
  // Convert standard YouTube watch link to embed format dynamically
  let displayUrl = content.url;
  if (content.type === "video" && displayUrl.includes("youtube.com/watch?v=")) {
    displayUrl = displayUrl.replace("watch?v=", "embed/");
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative flex h-full max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-[34px] bg-slate-900 shadow-2xl ring-1 ring-white/10">
        
        {/* Modal Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-white/10 bg-white/5 p-4 pl-6 sm:p-5 sm:pl-8">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-500/20 text-blue-400">
              {content.type === "video" ? "🎥" : "📑"}
            </span>
            <h3 className="text-lg sm:text-xl font-black text-white">{content.title}</h3>
          </div>
          <button
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 font-bold text-white transition hover:bg-red-500"
          >
            ✕
          </button>
        </div>

        {/* Modal Viewer Content */}
        <div className="flex-1 bg-black p-2 sm:p-6">
          {content.type === "pdf" ? (
            <iframe
              src={displayUrl}
              className="h-full w-full rounded-2xl bg-white"
              title={content.title}
            />
          ) : (
            <iframe
              src={displayUrl}
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              className="h-full w-full rounded-2xl bg-black border-none"
              title={content.title}
            />
          )}
        </div>
      </div>
    </div>
  );
};
// ----------------------------------------------------

const OverviewTab = ({ course, student, subjects, liveCount, recordingsCount, setActiveTab }: any) => (
  <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
    <section className="rounded-[34px] bg-white p-6 shadow-sm">
      <p className="text-sm font-black uppercase tracking-wide text-blue-600">Course Overview</p>
      <h2 className="mt-2 text-2xl font-black text-slate-950">{course.title}</h2>
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <QuickCard title="Subjects" value={String(subjects.length)} desc="Course syllabus" onClick={() => setActiveTab("subjects")} />
        <QuickCard title="Live Classes" value={String(liveCount)} desc="Upcoming/live" onClick={() => setActiveTab("live")} />
        <QuickCard title="Recordings" value={String(recordingsCount)} desc="Completed classes" onClick={() => setActiveTab("recordings")} />
      </div>
    </section>
    <section className="rounded-[34px] bg-white p-6 shadow-sm">
      <p className="text-sm font-black uppercase tracking-wide text-blue-600">Student Access</p>
      <div className="mt-5 space-y-4">
        <InfoRow label="Name" value={student.fullName || "Student"} />
        <InfoRow label="Class" value={student.studentClass || "--"} />
        <InfoRow label="Course" value={course.title} />
        <InfoRow label="Status" value="Active" />
      </div>
    </section>
  </div>
);

const SubjectsTab = ({ subjects }: any) => (
  <section className="rounded-[34px] bg-white p-6 shadow-sm">
    <p className="text-sm font-black uppercase tracking-wide text-blue-600">Subjects</p>
    <h2 className="mt-2 text-2xl font-black text-slate-950">Course Subjects</h2>
    <div className="mt-6 grid gap-5 xl:grid-cols-3">
      {subjects.map((item: any) => (
        <div key={item.title} className="rounded-[28px] border border-slate-100 bg-[#f8fbff] p-6">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-lg font-black text-blue-700">{item.icon}</div>
          <h3 className="mt-4 text-xl font-black text-slate-950">{item.title}</h3>
          <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">{item.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

const LiveTab = ({ liveNowClasses, upcomingClasses, joinLiveClass }: any) => (
  <section className="rounded-[34px] bg-white p-6 shadow-sm">
    <p className="text-sm font-black uppercase tracking-wide text-blue-600">Live Classes</p>
    <h2 className="mt-2 text-2xl font-black text-slate-950">Upcoming and Live Classes</h2>
    <div className="mt-6 grid gap-5 xl:grid-cols-2">
      {[...liveNowClasses, ...upcomingClasses].map((item: any) => (
        <LiveClassCard key={item.id} item={item} joinLiveClass={joinLiveClass} />
      ))}
    </div>
  </section>
);

// Update: Uses the new onWatch prop
const RecordingsTab = ({ completedClasses, onWatch }: { completedClasses: LiveClass[], onWatch: (url: string, title: string) => void }) => (
  <section className="rounded-[34px] bg-white p-6 shadow-sm">
    <p className="text-sm font-black uppercase tracking-wide text-blue-600">Recordings</p>
    <h2 className="mt-2 text-2xl font-black text-slate-950">Completed Class Recordings</h2>
    <div className="mt-6 grid gap-5 xl:grid-cols-2">
      {completedClasses.map((item) => (
        <div key={item.id} className="rounded-[28px] bg-[#f8fbff] p-6">
          <StatusBadge status={item.status} />
          <h3 className="mt-4 text-xl font-black text-slate-950">{item.title}</h3>
          <p className="mt-2 text-sm font-bold text-slate-500">{item.subject} • {item.teacher}</p>
          {item.recordingUrl ? (
            <button
              onClick={() => onWatch(item.recordingUrl!, item.title)}
              className="mt-5 inline-flex rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700 transition"
            >
              ▶ Watch Recording
            </button>
          ) : (
            <p className="mt-5 text-sm font-bold text-slate-400">Recording not uploaded yet.</p>
          )}
        </div>
      ))}
    </div>
  </section>
);

// Update: Uses the new onView prop
const NotesTab = ({ materials, onView }: { materials: MaterialItem[], onView: (url: string, title: string) => void }) => (
  <section className="rounded-[34px] bg-white p-6 shadow-sm">
    <p className="text-sm font-black uppercase tracking-wide text-blue-600">Study Notes</p>
    <h2 className="mt-2 text-2xl font-black text-slate-950">Notes, PDFs and Assignments</h2>
    <div className="mt-6 grid gap-5 xl:grid-cols-2">
      {materials.map((item) => (
        <div key={item.title} className="rounded-[28px] bg-[#f8fbff] p-6">
          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">{item.type}</span>
          <h3 className="mt-4 text-lg font-black text-slate-950">{item.title}</h3>
          <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">{item.desc}</p>
          <button 
             onClick={() => item.url && onView(item.url, item.title)}
             className="mt-5 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white hover:bg-blue-600 transition"
          >
            View Material
          </button>
        </div>
      ))}
    </div>
  </section>
);

const TestsTab = ({ course }: any) => (
  <section className="rounded-[34px] bg-white p-6 shadow-sm">
    <p className="text-sm font-black uppercase tracking-wide text-blue-600">Tests</p>
    <h2 className="mt-2 text-2xl font-black text-slate-950">Course Tests and Practice</h2>
    <div className="mt-6 grid gap-5 xl:grid-cols-3">
      <QuickCard title="Demo Quiz" value="1" desc="Available" />
      <QuickCard title="Mock Tests" value="3" desc="Practice set" />
      <QuickCard title="Reports" value="0" desc="After attempt" />
    </div>
  </section>
);

const ProgressTab = ({ course }: any) => (
  <section className="rounded-[34px] bg-white p-6 shadow-sm">
    <p className="text-sm font-black uppercase tracking-wide text-blue-600">Progress</p>
    <h2 className="mt-2 text-2xl font-black text-slate-950">Learning Progress</h2>
  </section>
);

const StatCard = ({ label, value }: { label: string; value: string }) => ( <div className="rounded-[24px] border border-white/15 bg-white/10 p-4 backdrop-blur"><p className="text-xs font-black uppercase tracking-wide text-blue-100">{label}</p><p className="mt-2 text-3xl font-black text-white">{value}</p></div> );
const QuickCard = ({ title, value, desc, onClick }: any) => ( <button onClick={onClick} className="rounded-[24px] bg-[#f8fbff] p-5 text-left transition hover:-translate-y-1 hover:bg-blue-50"><p className="text-sm font-bold text-slate-500">{title}</p><h3 className="mt-2 text-3xl font-black text-slate-950">{value}</h3><p className="mt-1 text-xs font-black text-emerald-600">{desc}</p></button> );
const InfoRow = ({ label, value }: { label: string; value: string }) => ( <div className="flex items-center justify-between gap-4 rounded-2xl bg-[#f6f9ff] px-4 py-3"><span className="text-sm font-bold text-slate-500">{label}</span><span className="text-right text-sm font-black text-slate-950">{value}</span></div> );
const InfoBox = ({ label, value }: { label: string; value: string }) => ( <div className="rounded-2xl bg-white p-4"><p className="text-xs font-bold text-slate-400">{label}</p><p className="mt-1 break-words text-sm font-black text-slate-900">{value || "--"}</p></div> );
const StatusBadge = ({ status }: { status: LiveStatus }) => ( <span className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${ status === "Live" ? "bg-red-50 text-red-700" : status === "Scheduled" ? "bg-blue-50 text-blue-700" : status === "Completed" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600" }`}>{status}</span> );
const LiveClassCard = ({ item, joinLiveClass }: any) => (
  <div className="rounded-[30px] border border-slate-100 bg-[#f8fbff] p-6">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <StatusBadge status={item.status} />
      <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">{item.platform}</span>
    </div>
    <h3 className="mt-4 text-xl font-black text-slate-950">{item.title}</h3>
    <p className="mt-2 text-sm font-bold text-slate-500">{item.subject} • {item.batch}</p>
    <div className="mt-5 grid gap-3 sm:grid-cols-2">
      <InfoBox label="Teacher" value={item.teacher} />
      <InfoBox label="Date" value={item.date} />
    </div>
  </div>
);

export default CourseDetails;